import { useCallback, useMemo } from 'react';
import { isIOS, isAndroid, isSamsungBrowser, isSafari, isChrome, isEdge, isChromium, isEdgeChromium } from 'react-device-detect';
import { stringify as qsStringify } from 'qs';
import {
	os,
	OS_ENUM,
	DEEP_LINK_PROTOCOL,
	GOOGLE_PLAY_APP_PATH,
	APP_STORE_APP_PATH,
	DASHBOARD_URL,
	GET_APP_PAGE_PATH,
} from '../../constants';
import {
	makeEventPageLink,
	makeHostPageLink,
	filterEmptyValues,
	configureOpenApp,
} from '@sweatpals/shared';

export const openDeeplinkBasedOnPlatform = ({
	url,
	openFallback,
	desktopUrl,
}: {
	url: string;
	openFallback: boolean;
	desktopUrl?: string;
}) => {
	const openApp = configureOpenApp({
		deviceDetect: {
			isIOS: isIOS,
			isAndroid: isAndroid,
			isSamsung: isSamsungBrowser,
			isSafari: isSafari,
			isChrome: isChrome || isEdge || isChromium || isEdgeChromium,
		},
		urlScheme: url,
		package: 'com.sweatpals.sweatpals_app',
		...(openFallback
			? {
					fallbackTimeout: 3000,
					fallbackAndroid: GOOGLE_PLAY_APP_PATH,
					fallbackIos: APP_STORE_APP_PATH,
					fallbackDesktop: desktopUrl,
			  }
			: {}),
	});

	openApp();
};

const useLink = () => {
	const downloadAppLink = useMemo(() => GET_APP_PAGE_PATH, []);

	const downloadAppLinkByOs = useMemo(
		() => ([OS_ENUM.iOS, OS_ENUM.MacOS].includes(os as OS_ENUM) ? APP_STORE_APP_PATH : GOOGLE_PLAY_APP_PATH),
		[]
	);

	const makeDashboardLink = useCallback(() => `${DASHBOARD_URL}`, []);

	const makeDashboardEventLink = useCallback(
		({ eventId, instance }: { eventId: string; instance: string | Date }) =>
			`${makeDashboardLink()}/event/${eventId}?instance=${
				instance instanceof Date ? instance.toISOString() : instance
			}`,
		[makeDashboardLink]
	);

	const makeDeepAppLink = useCallback(() => `${DEEP_LINK_PROTOCOL}://app/`, []);

	const makeDeepEventLink = useCallback(
		({ eventAlias, shortLocalInstance }: { eventAlias: string; shortLocalInstance?: string }) =>
			`${DEEP_LINK_PROTOCOL}://${makeEventPageLink({ eventAlias, shortLocalInstance })}`,
		[]
	);

	const makeDeepChatLink = useCallback((inviteCode: string) => `${DEEP_LINK_PROTOCOL}://chat/${inviteCode}`, []);

	const makeDeepProfileLink = useCallback((userName: string) => `${DEEP_LINK_PROTOCOL}://${makeHostPageLink({ userName })}`, []);

	const openDeepLink = useCallback(
		(
			options?: {
				isSpecificEventPage?: boolean;
				eventAlias?: string;
				shortLocalInstance?: string;

				isChatLink?: boolean;
				inviteCode?: string;

				isProfilePage?: boolean;
				userName?: string;
			},
			openFallback = true
		) => {
			const {
				isSpecificEventPage,
				eventAlias,
				shortLocalInstance,
				isChatLink,
				inviteCode,
				isProfilePage,
				userName,
			} = options || {};

			let deepLink = makeDeepAppLink();

			if (isSpecificEventPage && eventAlias) {
				deepLink = makeDeepEventLink({ eventAlias, shortLocalInstance });
			}

			if (isChatLink && inviteCode) {
				deepLink = makeDeepChatLink(inviteCode);
			}

			if (isProfilePage && userName) {
				deepLink = makeDeepProfileLink(userName);
			}

			openDeeplinkBasedOnPlatform({ url: deepLink, openFallback, desktopUrl: downloadAppLink });
		},
		[makeDeepAppLink, makeDeepEventLink, makeDeepChatLink, makeDeepProfileLink, downloadAppLink]
	);

	const makeSearchEventsLink = useCallback(
		({
			interests = [],
			place,
			fitnessTypes,
			...rest
		}: {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			[key: string]: any;
			place?: string;
			interests?: string[] | string;
			fitnessTypes?: { id: string }[];
		}): string => {
			if (typeof interests === 'string') {
				interests = [interests];
			}

			const queryParams = qsStringify(
				filterEmptyValues({
					...rest,
					interests: !place || interests.length > 1 ? interests.join(',') : undefined,
					fitnessTypeIds: rest?.fitnessTypeIds ?? fitnessTypes?.map(type => type.id),
				}),
				{ arrayFormat: 'repeat' }
			);

			return `/search${place ? `/${place}` : ''}${place && interests.length === 1 ? `/${interests[0]}` : ''}${
				queryParams ? `?${queryParams}` : ''
			}`;
		},
		[]
	);

	const makeEventLink = useCallback(
		({ eventAlias, shortLocalInstance }: Parameters<typeof makeEventPageLink>[0]) =>
			`/${makeEventPageLink({ eventAlias, shortLocalInstance })}`,
		[]
	);

	const makeHostLink = useCallback(({ userName }: Parameters<typeof makeHostPageLink>[0]) => `/${makeHostPageLink({ userName })}`, []);

	return {
		appLink: downloadAppLink,
		appLinkByOs: downloadAppLinkByOs,

		openDeepLink,
		makeDeepAppLink,
		makeDeepEventLink,
		makeDeepChatLink,
		makeDeepProfileLink,

		makeEventLink,
		makeSearchEventsLink,
		makeHostLink,

		makeDashboardLink,
		makeDashboardEventLink,
	};
};

export default useLink;
